import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "@/hooks/useSearchParams";
import { useSkrillDeposit } from "@/hooks/firestore/user/useSkrillDeposits";
import {
  depositModalParams,
  hasDepositReturnParams,
} from "../models/depositModalParams";
import type { DepositMethodId, SelectableMethodDescriptor } from "../types";
import type { DepositMethodStepName } from "../utils/registry";

const SETTLED_SKRILL_STATUSES = ["PROCESSED", "PENDING", "FAILED"];

// How long to wait for the Skrill transaction doc to populate in Firestore
const TRANSACTION_TIMEOUT_MS = 10_000;

export type DepositReturnState = {
  methodId?: DepositMethodId;
  transactionId?: string;
  cancelled: boolean;
};

/**
 * Owns everything the wizard knows about coming back from an off-site payment
 * redirect. The params are consumed on first render and cleared from the url
 * immediately, so the outcome lives in React state only: reopening the modal
 * or refreshing the page can't replay a stale result.
 */
export const useSkrillDepositReturn = (
  availableMethods: readonly SelectableMethodDescriptor[],
) => {
  const { searchParams, setSearchParams } = useSearchParams(depositModalParams);
  const { method, return: hasReturned, cancel, transactionId } = searchParams;

  const [captured] = useState(() => ({
    method,
    transactionId,
    returned: Boolean(hasReturned),
    cancelled: Boolean(cancel),
    hasReturnParams: hasDepositReturnParams(searchParams),
  }));

  useEffect(() => {
    if (!captured.hasReturnParams) {
      return;
    }

    setSearchParams(
      {
        return: undefined,
        cancel: undefined,
        transactionId: undefined,
        method: undefined,
        depositModal: undefined,
      },
      { replace: true },
    );
  }, []);

  const capturedMethod =
    captured.method ??
    ((captured.returned || captured.cancelled) && captured.transactionId
      ? "skrill"
      : undefined);

  const methodId = availableMethods.find(
    (available) => available.id === capturedMethod,
  )?.id;

  const isSkrillReturn = methodId === "skrill" && !captured.cancelled;

  const { deposit: transaction, isLoading: transactionLoading } =
    useSkrillDeposit(isSkrillReturn ? captured.transactionId : undefined);

  const [waitExpired, setWaitExpired] = useState(false);

  useEffect(() => {
    if (!isSkrillReturn || !captured.returned) {
      return;
    }

    const timer = setTimeout(
      () => setWaitExpired(true),
      TRANSACTION_TIMEOUT_MS,
    );

    return () => clearTimeout(timer);
  }, [isSkrillReturn, captured.returned]);

  const awaitingTransaction =
    isSkrillReturn &&
    captured.returned &&
    !waitExpired &&
    (transactionLoading || !transaction);

  const gaveUpWaiting =
    isSkrillReturn && captured.returned && waitExpired && !transaction;

  const initialStep =
    (methodId === "skrill" && captured.cancelled) ||
    (transaction && SETTLED_SKRILL_STATUSES.includes(transaction.status)) ||
    gaveUpWaiting
      ? ("skrill/status" satisfies DepositMethodStepName)
      : ("deposit" satisfies DepositMethodStepName);

  const depositReturn = useMemo<DepositReturnState>(
    () => ({
      methodId,
      transactionId: captured.transactionId,
      cancelled: captured.cancelled,
    }),
    [methodId, captured],
  );

  return {
    isSettled: !awaitingTransaction,
    initialMethodId: methodId,
    initialStep,
    depositReturn,
  };
};
